import { Dialog, DialogBareContent, DialogOverlay, DialogPortal, DialogTitle } from '@h2hspark/ui';
import { ArrowLeft, X } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useOverlayFocusReturn } from '../../member/use-overlay-focus-return';

import { MESSAGES_PATH, THREAD_BACK_LABEL } from './constants';
import { ThreadPanel } from './thread-panel';

/**
 * One thread, opened over the Messages page — `MEM-054` AC-2: *"a full-screen view on phone and a
 * dialog on desktop"*, with a back control.
 *
 * ## Why it is a route rather than a piece of the page's state
 *
 * `MEM-055` AC-6: the new-message notification **opens that thread** — directly for a signed-in
 * member, and after sign-in otherwise. A thread held in the Messages page's state has no address
 * for a notification to land on, and `MEM-047`'s deep links are addresses. So the thread is
 * `/app/messages/:connectionRef`, nested under the page, and the page renders it through its
 * `<Outlet/>`: the list stays mounted behind the dialog on desktop, which is what keeps
 * `MEM-042` AC-6's *"back returns to the list at its previous scroll position"* true without a
 * scroll-restoration trick.
 *
 * ## One element, two shapes
 *
 * Radix gives the behaviour a dialog is used for at either size — the focus trap, Escape, the
 * scrim click — and none of that is less true on a phone.
 *
 * **Focus return is not Radix's here, and this header used to claim it was.** The thread is a
 * route: closing it navigates, the Messages list re-renders, and the row that opened the thread is
 * a *new* node — so the element Radix held a reference to is detached, focusing it does nothing,
 * and the keyboard landed on `<body>`. The parity pass of 2026-09-12 found it by pressing Escape
 * and reading `document.activeElement`, which is the only thing that would have.
 * `useOverlayFocusReturn` takes it over; see that hook's header. What
 * changes is geometry: `inset-0` and square below `md`, a centred 460px card above it. `MEM-056`
 * AC-2's reflow, not a second component.
 *
 * **The close control changes with it, and that is a capability rather than a style.** On a phone
 * the way out of a full-screen view is a back arrow at the top left, where a thumb is and where
 * the platform's own back gesture lives; on a desktop dialog it is the ✕ at the top right, over a
 * scrim that also closes. Both land on the Messages page.
 */
export function ThreadDialog(): React.ReactElement | null {
  const { connectionRef } = useParams<{ connectionRef: string }>();
  const navigate = useNavigate();
  const close = React.useCallback(() => {
    void navigate(MESSAGES_PATH);
  }, [navigate]);
  // The row that opened this links to the thread's own address, which survives the remount the
  // navigation causes when nothing else about the row does.
  const { onCloseAutoFocus } = useOverlayFocusReturn(useLocation().pathname);

  if (connectionRef === undefined || connectionRef === '') return null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogPortal>
        {/* The scrim is drawn from `md` up. Below it the panel is the whole screen, so a scrim
            behind it would be a tap target nobody can reach. */}
        <DialogOverlay className="hidden md:block" />
        <DialogBareContent
          className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-card shadow-xl md:inset-auto md:left-1/2 md:top-1/2 md:h-auto md:max-h-[80vh] md:w-full md:max-w-[460px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-xl"
          aria-describedby={undefined}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          {/* The accessible name of the dialog. Visually the header inside `ThreadPanel` carries
              the person; this is what a screen reader announces on open. */}
          <DialogTitle className="sr-only">{THREAD_BACK_LABEL}</DialogTitle>

          <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2 md:hidden">
            <button
              type="button"
              onClick={close}
              className="inline-flex items-center gap-1.5 rounded-pill px-2 py-1.5 font-brand text-sm font-bold text-primary"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              {THREAD_BACK_LABEL}
            </button>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute right-3 top-3 z-10 hidden h-8 w-8 items-center justify-center rounded-pill text-muted-foreground hover:bg-ivory-100 md:inline-flex"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>

          <ThreadPanel connectionRef={connectionRef} variant="overlay" />
        </DialogBareContent>
      </DialogPortal>
    </Dialog>
  );
}
